import { useMemo } from 'react';

import { PlanUsageLimit, PlanUsageResourceKey } from '@/constants';
import { getResolvedResourceLimit } from '@/shared/entitlements';
import {
    getPlanLabelFromUserPlan,
    getPlanUsageLimit,
    PLAN_USAGE_RESOURCE_LABELS,
    PLAN_USAGE_RESOURCE_TO_PLAN_LABEL,
} from '@/shared/pricing';
import { useUserStore } from '@/stores';

import { useBillingSnapshot } from './useBillingSnapshot';
import { useWorkspaceResources } from './useWorkspaceResources';

export type PlanUsageState = {
    count: number;
    limit: PlanUsageLimit;
    limitLabel: string;
    planLabel: string;
    remaining: number | null;
    resourceLabel: string;
    reached: boolean;
    unlimited: boolean;
    reason?: string;
};

const getResourceCount = (resourcesCount: Record<string, unknown>, resource: PlanUsageResourceKey) => {
    const rawCount = resourcesCount?.[resource];

    if (typeof rawCount === 'number') {
        return rawCount;
    }
    if (typeof rawCount === 'string') {
        return Number(rawCount) || 0;
    }

    return 0;
};

export const usePlanUsageLimits = () => {
    const { selectedUser } = useUserStore();
    const [resourcesCount] = useWorkspaceResources();
    const { snapshot } = useBillingSnapshot();

    return useMemo(() => {
        const resolvedPlan = snapshot?.plan?.code ?? selectedUser?.plan;
        const planLabel = getPlanLabelFromUserPlan(resolvedPlan);

        const getUsageState = (resource: PlanUsageResourceKey, countOverride?: number): PlanUsageState => {
            const count = countOverride ?? getResourceCount(resourcesCount, resource);
            const limit = getResolvedResourceLimit(snapshot, resource) ?? getPlanUsageLimit(resolvedPlan, resource);
            const unlimited = limit === 'unlimited';
            const reached = !unlimited && count >= limit;
            const resourceLabel = PLAN_USAGE_RESOURCE_LABELS[resource];
            const planResourceLabel = PLAN_USAGE_RESOURCE_TO_PLAN_LABEL[resource];
            const limitLabel = unlimited ? 'Unlimited' : `${count}/${limit}`;

            return {
                count,
                limit,
                limitLabel,
                planLabel,
                remaining: unlimited ? null : Math.max(limit - count, 0),
                resourceLabel,
                reached,
                unlimited,
                reason: reached
                    ? `${planLabel} workspaces can create up to ${limit} ${resourceLabel}. Upgrade to create more ${planResourceLabel}.`
                    : undefined,
            };
        };

        return {
            getUsageState,
            isResourceLimitReached: (resource: PlanUsageResourceKey) => getUsageState(resource).reached,
        };
    }, [resourcesCount, selectedUser?.plan, snapshot]);
};
